import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import booksData from "../data/books.json";
import LessonSidebar from "../components/BooksReader/LessonSidebar";

const slugify = (s = "") =>
  String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function Reader() {
  const { slug, chapterIndex } = useParams();
  const navigate = useNavigate();

  const HEADER_OFFSET = "10vh"; // altura del navbar fijo

  const books = Array.isArray(booksData) ? booksData : [];
  const normalized = useMemo(
    () =>
      books.map((b) => ({
        ...b,
        _slug: b.slug || b.id || slugify(b.title || ""),
      })),
    [books]
  );

  const book = normalized.find((b) => b._slug === slug) || null;

  // chapters seguros aun si no hay libro
  const chapters = Array.isArray(book?.chapters) ? book.chapters : [];

  // índice seguro desde la URL
  const parsed = Number.isInteger(Number(chapterIndex))
    ? Number(chapterIndex)
    : 0;
  const clamped = Math.max(0, Math.min(parsed, Math.max(0, chapters.length - 1)));

  // Redirige a un chapterIndex válido, pero NUNCA condiciona el hook
  useEffect(() => {
    if (!book) return; // si no hay libro, no navegamos
    if (String(clamped) !== String(chapterIndex)) {
      navigate(`/reader/${book._slug}/${clamped}`, { replace: true });
    }
  }, [book, clamped, chapterIndex, navigate]);

  const current =
    book && chapters.length
      ? chapters[clamped]
      : { title: "Sin capítulos", content: "…" };

  const go = (i) => navigate(`/reader/${book?._slug || slug}/${i}`);

  // Estructura para la sidebar (solo si hay libro)
  const bookWithSections = book
    ? {
        ...book,
        sections: [
          {
            title: "Capítulos",
            entries: chapters.map((c, i) => ({
              slug: String(i),
              label: `${c?.index ? `${c.index}. ` : ""}${c?.title || `Capítulo ${i + 1}`}`,
            })),
          },
        ],
      }
    : null;

  const styles = {
    app: {
      display: "grid",
      gridTemplateColumns: "280px 1fr",
      height: `calc(100vh - ${HEADER_OFFSET})`,
      marginTop: HEADER_OFFSET,
    },
    main: { padding: "24px", overflowY: "auto" },
    h1: { margin: "0 0 8px", color: "#0f172a" },
    meta: { color: "#475569", margin: "0 0 16px" },
    content: { lineHeight: 1.6, color: "#1f2937", whiteSpace: "pre-wrap" },
    navRow: { display: "flex", gap: "8px", marginTop: "24px" },
    btn: {
      padding: "10px 14px",
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
      background: "#fff",
      cursor: "pointer",
      textDecoration: "none",
      color: "#111827",
    },
  };

  return (
    <div style={styles.app}>
      {bookWithSections ? (
        <LessonSidebar
          book={bookWithSections}
          headerOffset={HEADER_OFFSET}
          baseSlug={book._slug}
        />
      ) : (
        <aside style={{ padding: 16, borderRight: "1px solid #e6ebf1" }}>
          <div style={{ fontWeight: 800, color: "#175cd3" }}>Curso</div>
          <div style={{ color: "#6b7280", marginTop: 8 }}>Sin contenido</div>
        </aside>
      )}

      <main style={styles.main}>
        {!book ? (
          <>
            <h2>No encontrado</h2>
            <Link to="/course/books">← Volver</Link>
          </>
        ) : (
          <>
            <h1 style={styles.h1}>{current.title}</h1>
            <p style={styles.meta}>
              {book.authors?.join(", ") || "—"} · {book.language?.toUpperCase() || "—"}
            </p>
            <article style={styles.content}>
              {current.content || "Contenido del capítulo aquí…"}
            </article>

            <div style={styles.navRow}>
              <button
                style={styles.btn}
                disabled={clamped <= 0}
                onClick={() => go(clamped - 1)}
              >
                ← Anterior
              </button>
              <button
                style={styles.btn}
                disabled={clamped >= chapters.length - 1}
                onClick={() => go(clamped + 1)}
              >
                Siguiente →
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
